import React, { useState, useEffect } from "react";
import { db } from "./firebase";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { collection, serverTimestamp, doc, setDoc, getDocs, deleteDoc, addDoc } from "firebase/firestore";

export default function AdminNewsletters() {
    const [formData, setFormData] = useState({
        title: "",
    });
    const [pdfFile, setPdfFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [newsletters, setNewsletters] = useState([]);
    const [editingId, setEditingId] = useState(null);

    const storage = getStorage();

    const fetchNewsletters = async () => {
        try {
            const querySnapshot = await getDocs(collection(db, "newsletters"));
            const fetched = querySnapshot.docs.map(doc => ({ ...doc.data(), dbId: doc.id }));
            // sort by createdAt descending
            fetched.sort((a, b) => {
                const timeA = a.createdAt?.toMillis() || 0;
                const timeB = b.createdAt?.toMillis() || 0;
                return timeB - timeA;
            });
            setNewsletters(fetched);
        } catch (error) {
            console.error("Failed to fetch newsletters:", error);
        }
    };

    useEffect(() => {
        fetchNewsletters();
    }, []);

    const handleInput = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleFileChange = (e) => {
        if (e.target.files[0]) {
            setPdfFile(e.target.files[0]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage("");

        try {
            let pdfUrl = formData.pdfUrl || "";

            if (pdfFile) {
                const pdfRef = ref(storage, `newsletters/${Date.now()}_${pdfFile.name}`);
                await uploadBytes(pdfRef, pdfFile);
                pdfUrl = await getDownloadURL(pdfRef);
            }

            const numericId = editingId ? formData.id : Date.now();

            const newsletterData = {
                id: numericId,
                title: formData.title,
                pdfUrl: pdfUrl,
                updatedAt: serverTimestamp()
            };

            if (!editingId) {
                newsletterData.createdAt = serverTimestamp();
                await addDoc(collection(db, "newsletters"), newsletterData);
                setMessage("Newsletter successfully added!");
            } else {
                await setDoc(doc(db, "newsletters", editingId), newsletterData, { merge: true });
                setMessage("Newsletter successfully updated!");
            }
            
            cancelEdit();
            fetchNewsletters(); 
        } catch (error) {
            console.error("Error saving newsletter:", error);
            setMessage("Failed to save newsletter: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (n) => {
        setEditingId(n.dbId);
        setFormData({
            id: n.id,
            title: n.title || "",
            pdfUrl: n.pdfUrl || ""
        });
        setPdfFile(null);
        setMessage("Editing newsletter: " + n.title);
    };

    const handleDelete = async (n) => {
        if (!window.confirm("Are you sure you want to delete '" + n.title + "'?")) return;
        try {
            await deleteDoc(doc(db, "newsletters", n.dbId));
            setMessage("Newsletter deleted.");
            fetchNewsletters();
        } catch (error) {
            console.error("Error deleting:", error);
            setMessage("Failed to delete.");
        }
    };

    const cancelEdit = () => {
        setEditingId(null);
        setFormData({ title: "", pdfUrl: "" });
        setPdfFile(null);
        setMessage("");
    };

    const styles = {
        container: { padding: '40px', maxWidth: '1200px', margin: '0 auto', fontFamily: "'Syne', sans-serif" },
        header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' },
        title: { fontSize: '2.5rem', fontWeight: '400', color: '#003049', margin: 0 },
        mainFlex: { display: 'flex', gap: '40px', alignItems: 'flex-start', flexWrap: 'wrap' },
        formSection: { flex: 1, minWidth: '400px', background: 'white', padding: '30px', borderRadius: '15px', boxShadow: '0 10px 30px rgba(0,0,0,0.05)' },
        listSection: { flex: 1, minWidth: '400px', padding: '10px', height: '800px', overflowY: 'auto' },
        inputGroup: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '15px' },
        label: { fontSize: '14px', fontWeight: '600', color: '#003049' },
        input: { padding: '12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', fontFamily: "'Syne', sans-serif", outline: 'none' },
        submitBtn: { padding: '15px', background: '#F07167', color: 'white', borderRadius: '30px', cursor: 'pointer', border: 'none', fontWeight: 'bold', fontSize: '16px', width: '100%', marginTop: '10px', boxShadow: '0 4px 15px rgba(240,113,103,0.3)' },
        msg: { padding: '15px', background: '#eefbf3', color: '#155724', borderRadius: '8px', marginBottom: '20px', border: '1px solid #c8e6c9', fontSize: '14px' },
        errorMsg: { padding: '15px', background: '#faeced', color: '#721c24', borderRadius: '8px', marginBottom: '20px', border: '1px solid #f5c6cb', fontSize: '14px' },
        listTitle: { fontSize: '1.5rem', fontWeight: '400', color: '#003049', marginBottom: '20px' },
        card: { padding: '20px', border: 'none', borderRadius: '15px', marginBottom: '15px', background: 'white', display: 'flex', gap: '20px', alignItems: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', position: 'relative' },
        info: { flex: 1 },
        name: { margin: '0 0 5px 0', fontSize: '16px', fontWeight: '600', color: '#003049', paddingRight: '60px' },
        details: { margin: '0', fontSize: '13px', color: '#666', lineHeight: '1.6', wordBreak: 'break-all' },
        cardActions: { position: 'absolute', top: '20px', right: '20px', display: 'flex', flexDirection: 'row', gap: '10px' },
        actionBtn: { padding: '6px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#003049' },
        deleteBtn: { padding: '6px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#F07167' }
    };

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.title}>{editingId ? "Edit Newsletter" : "Add New Newsletter"}</h1>
            </div>

            <div style={styles.mainFlex}>
                <div style={styles.formSection}>
                    {message && (
                        <div style={message.includes("Failed") ? styles.errorMsg : styles.msg}>
                            {message}
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>Title (e.g. May, June)</label>
                            <input name="title" value={formData.title} onChange={handleInput} style={styles.input} required placeholder="Newsletter Title" />
                        </div>
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>Newsletter PDF File</label>
                            <input type="file" accept="application/pdf" onChange={handleFileChange} style={styles.input} required={!editingId && !formData.pdfUrl} />
                            {formData.pdfUrl && <small style={{color: '#666'}}>Current PDF uploaded.</small>}
                        </div>

                        <div style={{display: 'flex', gap: '10px'}}>
                            <button type="submit" style={styles.submitBtn} disabled={loading}>
                                {loading ? "Saving..." : (editingId ? "Update Newsletter" : "Add Newsletter")}
                            </button>
                            {editingId && (
                                <button type="button" onClick={cancelEdit} style={{...styles.submitBtn, background: '#eee', color: '#333', boxShadow: 'none'}}>
                                    Cancel
                                </button>
                            )}
                        </div>
                    </form>
                </div>

                <div className="no-scrollbar" style={styles.listSection}>
                    <h2 style={styles.listTitle}>Live Newsletters ({newsletters.length})</h2>
                    {newsletters.length === 0 ? (
                        <p style={{ fontSize: '14px', color: '#777' }}>No newsletters found.</p>
                    ) : (
                        newsletters.map(n => (
                            <div key={n.dbId} style={styles.card}>
                                <div style={styles.info}>
                                    <h3 style={styles.name}>{n.title}</h3>
                                    <p style={styles.details}>
                                        <a href={n.pdfUrl} target="_blank" rel="noopener noreferrer" style={{color: '#F07167'}}>View PDF</a>
                                    </p>
                                </div>
                                <div style={styles.cardActions}>
                                    <button onClick={() => handleEdit(n)} style={styles.actionBtn} title="Edit">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M12 20h9"></path>
                                            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                                        </svg>
                                    </button>
                                    <button onClick={() => handleDelete(n)} style={styles.deleteBtn} title="Delete">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="3 6 5 6 21 6"></polyline>
                                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}
